import type { FileStatusId } from '@steerfi/shared';
import type { InstructionDetails } from '@steerfi/shared/types/jsonb/payment-instruction.types';
import type { PartySnapshot as ParticipantPartySnapshot } from '@steerfi/shared/types/jsonb/payment-instruction-participant.types';
import type { EventLogEntry } from '@/components/event-timeline';
import keycloakApi from './keycloak-auth';

/**
 * Payment API Client
 * Interfaces with the new Payment infrastructure backend at /api/payments
 * Uses new Payment, PaymentInstruction, PaymentInstructionParticipant, PaymentItem entities
 */

// ============================================================================
// Type Definitions - Mirror backend DTOs
// ============================================================================

/**
 * Party snapshot (for owner, payer, payee relations)
 */
export interface PartySnapshot {
  id: string;
  external_id: string | null;
  party_name: string;
  party_type_id?: number;
}

/**
 * Status snapshot
 */
export interface StatusSnapshot {
  id: number;
  code: string;
  name: string;
  description?: string | null;
}

/**
 * Currency snapshot
 */
export interface CurrencySnapshot {
  id: number;
  code: string;
  name: string;
  symbol?: string;
}

/**
 * PaymentInstruction with relations
 */
export interface PaymentInstructionWithRelations {
  id: string;
  payment_id: string;
  owner_party_id: string;
  payer_account_id: string | null;
  payee_account_id: string | null;
  integration_profile_id: string | null;
  payment_method_id: number | null;
  status_id: number;
  amount: string;
  currency_id: number;
  direction: 'DEBIT' | 'CREDIT';
  execution_date: string | null;
  idempotency_key: string | null;
  instruction_details_json: InstructionDetails | null;
  validation_results_json: Record<string, unknown> | null;
  routing_snapshot_json: Record<string, unknown> | null;
  created_by_name: string | null;
  created_by_id: string | null;
  updated_by_name: string | null;
  updated_by_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  status: StatusSnapshot;
  currency: CurrencySnapshot;
  payment_method?: { id: number; name: string; code: string } | null;
  participant_count?: number;
  allocation_count?: number;
  participants?: Array<{
    id: number;
    participant_role: 'PAYER' | 'PAYEE' | 'INTERMEDIARY' | 'BENEFICIARY';
    participant_direction: 'DEBIT' | 'CREDIT';
    party_snapshot_json: ParticipantPartySnapshot;
  }>;
}

/**
 * PaymentItem with relations
 */
export interface PaymentItemWithRelations {
  id: number;
  payment_id: string;
  item_id: string | null;
  item_type_id: number;
  item_sequence: number;
  quantity: string;
  unit_amount: string;
  item_gross_amount: string;
  item_discount: string;
  item_net_amount: string;
  item_due_date: string | null;
  item_account_number: string | null;
  item_description: string | null;
  item_metadata_json: Record<string, unknown> | null;
  created_by_name: string | null;
  created_by_id: string | null;
  updated_by_name: string | null;
  updated_by_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  item_type: {
    id: number;
    code: string;
    name: string;
  };
  allocation_count?: number;
}

/**
 * Payment metadata JSONB structure
 */
export interface PaymentMetadata {
  tags?: string[];
  source?: string;
  original_file_name?: string;
  original_file_id?: string;
  processor_version?: string;
  processing_timestamp?: string;
  [key: string]: unknown;
}

/**
 * Basic Payment Response (without relations)
 */
export interface PaymentReturnInfo {
  return_code: string | null;
  return_reason: string;
  returned_at: string | null;
  disbursement_date: string | null;
}

// Mirrors Zod schemas in services/api-server/src/dtos/payment.dto.ts
// (ReissueBlockReasonSchema, ReissueMethodSnapshotSchema, ReissueEligibilitySchema).
// Keep in sync manually until these types live in a shared package.
export type ReissueBlockReason =
  | 'NOT_RETURNED'
  | 'PAYEE_MISSING'
  | 'PAYEE_INACTIVE'
  | 'METHOD_NEEDS_REVIEW'
  | 'METHOD_UNCHANGED'
  | 'NOT_APPROVED'
  | 'NO_ELIGIBLE_METHOD'
  | 'DIGITAL_WALLET_NOT_SUPPORTED'
  | 'REISSUE_ALREADY_IN_FLIGHT'
  | 'ALREADY_REISSUED'
  | 'SOURCE_FILE_NOT_FOUND'
  | 'SOURCE_ROW_NOT_FOUND'
  | 'PROVIDER_NOT_SUPPORTED';

export interface ReissueMethodSnapshot {
  id: string;
  type: string;
  label: string;
  masked_detail: string | null;
  review_status?: 'NEEDS_REVIEW' | 'CLEARED' | null;
  return_code?: string | null;
  return_reason?: string | null;
  returned_at?: string | null;
  eligible_for_reissue: boolean;
}

export interface PaymentReissueEligibility {
  payment_id: string;
  can_reissue: boolean;
  block_reason: ReissueBlockReason | null;
  /** Server-authored user-facing copy for the current block_reason; null when can_reissue. */
  block_message: string | null;
  supplier: {
    id: string;
    name: string;
    relationship_approval: string | null;
    payment_method_needs_review: boolean;
  } | null;
  returned_method: ReissueMethodSnapshot | null;
  approved_methods: ReissueMethodSnapshot[];
  return_info: PaymentReturnInfo | null;
}

export interface ReissuePaymentResponse {
  parent_payment_id: string;
  reissued_payment_id: string;
  reissue_file_id: string;
  reissue_file_name: string;
}

export interface PaymentResponse {
  id: string;
  owner_party_id: string | null;
  parent_payment_id?: string | null;
  original_payment_id?: string | null;
  external_id: string | null;
  external_reference: string | null;
  idempotency_key: string | null;
  payer_party_id: string;
  payee_party_id: string | null;
  transaction_type: string | null;
  status_id: number;
  direction: 'DEBIT' | 'CREDIT';
  amount_total: string;
  currency_id: number;
  execution_date_requested: string | null;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  metadata_json: PaymentMetadata | null;
  notes: string | null;
  created_by_name: string | null;
  created_by_id: string | null;
  updated_by_name: string | null;
  updated_by_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Payment with all relations populated
 * Includes arrays of related payment instructions and items
 */
export interface PaymentWithRelations extends PaymentResponse {
  owner_party: PartySnapshot | null;
  payer_party: PartySnapshot;
  payee_party: PartySnapshot | null;
  // Computed field: payee name derived from payee_party or instruction participant snapshot
  payee_name?: string | null;
  status: StatusSnapshot;
  currency: CurrencySnapshot;
  instruction_count?: number;
  item_count?: number;
  payment_method?: { id: number; name: string; code: string } | null;
  instructions?: PaymentInstructionWithRelations[];
  items?: PaymentItemWithRelations[];
  /** Present when status is RETURNED — backs the payment-detail banner. */
  return_info?: PaymentReturnInfo | null;
  /** Present when status is RETURNED — initializes the reissue modal without another request. */
  reissue_eligibility?: PaymentReissueEligibility | null;
  /** Live hand-off reissue child — Status & Priority "Reissued as" link. */
  reissued_as_payment_id?: string | null;
  reissued_as_external_id?: string | null;
  /** Parent external_id when parent_payment_id is set — "Reissued from" label. */
  parent_external_id?: string | null;
  file?: {
    id: string;
    file_name: string;
    logical_name: string;
    file_url: string;
    file_size_bytes: string | null;
    file_format_id: number;
    file_direction: string;
    status_type_id: number;
    created_at: string;
  } | null;
}

/**
 * Pagination metadata
 */
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * Paginated response wrapper
 * Backend returns: { success, message, data: [...], pagination }
 * authenticatedApiCall unwraps to: [...] (just the data array)
 * But we need the full response with pagination, so we use the raw backend structure
 */
export interface PaginatedPaymentsResponse {
  success: boolean;
  message: string;
  data: PaymentWithRelations[];
  pagination: PaginationInfo;
}

/**
 * Single payment response wrapper
 * Backend returns: { success, message, data: {...} }
 * authenticatedApiCall unwraps to: {...} (just the data object)
 */
export interface SinglePaymentResponse {
  success: boolean;
  message: string;
  data: PaymentWithRelations;
}

/**
 * Payment statistics response
 * Backend returns: { success, message, data: {...} }
 * We keep the full structure for consistency
 */
export interface PaymentStatsResponse {
  success: boolean;
  message: string;
  data: {
    total_count: number;
    total_amount: string;
    status_breakdown: Array<{
      status_id: number;
      status_name: string;
      count: number;
      total_amount: string;
    }>;
    direction_breakdown: Array<{
      direction: 'DEBIT' | 'CREDIT';
      count: number;
      total_amount: string;
    }>;
    priority_breakdown: Array<{
      priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
      count: number;
      total_amount: string;
    }>;
  };
}

/**
 * Filter parameters for payment queries
 */
export interface PaymentFilters {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
  owner_party_id?: string;
  status_id?: number | number[];
  payer_party_id?: string;
  payee_party_id?: string;
  direction?: 'DEBIT' | 'CREDIT';
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  search?: string;
  /** Narrow text search to one field when a prefix was used (payee / payer / ref). */
  search_field?: 'payee' | 'payer' | 'ref';
  payment_method_code?: string;
  /** ISO date string (YYYY-MM-DD) for inclusive lower bound on created_at. */
  from_date?: string;
  /** ISO date string (YYYY-MM-DD) for inclusive upper bound on created_at. */
  to_date?: string;
}

/**
 * Update payment status request
 */
export interface UpdatePaymentStatusRequest {
  status_id: number;
  notes?: string;
  updated_by_name?: string;
  updated_by_id?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get paginated list of payments with filtering
 * @param filters - Query parameters for filtering and pagination
 * @returns Paginated list of payments with relations
 */
export const getPayments = async (
  filters: PaymentFilters = {}
): Promise<PaginatedPaymentsResponse> => {
  const searchParams = new URLSearchParams();

  // Add pagination parameters
  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.limit) searchParams.append('limit', filters.limit.toString());
  if (filters.sort_by) searchParams.append('sort_by', filters.sort_by);
  if (filters.sort_order) searchParams.append('sort_order', filters.sort_order);

  // Add filter parameters
  if (filters.owner_party_id) searchParams.append('owner_party_id', filters.owner_party_id);
  if (filters.payer_party_id) searchParams.append('payer_party_id', filters.payer_party_id);
  if (filters.payee_party_id) searchParams.append('payee_party_id', filters.payee_party_id);
  if (filters.direction) searchParams.append('direction', filters.direction);
  if (filters.priority) searchParams.append('priority', filters.priority);
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.search_field) searchParams.append('search_field', filters.search_field);
  if (filters.payment_method_code)
    searchParams.append('payment_method_code', filters.payment_method_code);

  // Handle status_id - can be single number or array
  if (filters.status_id !== undefined) {
    if (Array.isArray(filters.status_id)) {
      filters.status_id.forEach((id) => searchParams.append('status_id', id.toString()));
    } else {
      searchParams.append('status_id', filters.status_id.toString());
    }
  }

  if (filters.from_date) searchParams.append('from_date', filters.from_date);
  if (filters.to_date) searchParams.append('to_date', filters.to_date);

  const queryString = searchParams.toString();
  const endpoint = `/payments${queryString ? `?${queryString}` : ''}`;

  return keycloakApi.authenticatedApiCall<PaginatedPaymentsResponse>(endpoint, {
    method: 'GET',
  });
};

/**
 * Get payment by ID with all relations
 * @param id - Payment UUID
 * @returns Payment with all related entities
 */
export const getPaymentById = async (id: string): Promise<PaymentWithRelations> => {
  // authenticatedApiCall normalizes `{ success, message, data }` responses by
  // unwrapping to `data` (no pagination present), so this resolves to the
  // payment object directly.
  return keycloakApi.authenticatedApiCall<PaymentWithRelations>(`/payments/${id}`, {
    method: 'GET',
  });
};

export const getPaymentReissueEligibility = async (
  id: string
): Promise<PaymentReissueEligibility> => {
  return keycloakApi.authenticatedApiCall<PaymentReissueEligibility>(
    `/payments/${id}/reissue-eligibility`,
    { method: 'GET' }
  );
};

export const reissuePayment = async (
  id: string,
  paymentMethodAccountId: string
): Promise<ReissuePaymentResponse> => {
  return keycloakApi.authenticatedApiCall<ReissuePaymentResponse>(`/payments/${id}/reissue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payment_method_account_id: paymentMethodAccountId }),
  });
};

/**
 * Get payments associated with a specific file
 * @param fileId - File UUID (from FileEntityAssociation)
 * @param filters - Optional pagination and sorting parameters
 * @returns Paginated list of payments linked to the file
 */
export const getPaymentsByFileId = async (
  fileId: string,
  filters: Omit<
    PaymentFilters,
    | 'owner_party_id'
    | 'status_id'
    | 'payer_party_id'
    | 'payee_party_id'
    | 'direction'
    | 'priority'
    | 'search'
  > = {}
): Promise<PaginatedPaymentsResponse> => {
  const searchParams = new URLSearchParams();

  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.limit) searchParams.append('limit', filters.limit.toString());
  if (filters.sort_by) searchParams.append('sort_by', filters.sort_by);
  if (filters.sort_order) searchParams.append('sort_order', filters.sort_order);

  const queryString = searchParams.toString();
  const endpoint = `/payments/file/${fileId}${queryString ? `?${queryString}` : ''}`;

  return keycloakApi.authenticatedApiCall<PaginatedPaymentsResponse>(endpoint, {
    method: 'GET',
  });
};

/**
 * Get payment statistics for a specific owner party
 * @param ownerPartyId - Party UUID
 * @returns Aggregated statistics for payments
 */
export const getPaymentStats = async (ownerPartyId: string): Promise<PaymentStatsResponse> => {
  return keycloakApi.authenticatedApiCall<PaymentStatsResponse>(`/payments/stats/${ownerPartyId}`, {
    method: 'GET',
  });
};

/**
 * Update payment status (administrative action)
 * @param id - Payment UUID
 * @param data - Status update request
 * @returns Updated payment with relations
 */
export const updatePaymentStatus = async (
  id: string,
  data: UpdatePaymentStatusRequest
): Promise<SinglePaymentResponse> => {
  return keycloakApi.authenticatedApiCall<SinglePaymentResponse>(`/payments/${id}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

/**
 * Get a pre-signed download URL for a payment receipt PDF.
 * Returns null if no receipt has been generated yet.
 */
export const getPaymentReceiptUrl = async (
  id: string
): Promise<{ signed_url: string; expires_in: number } | null> => {
  try {
    return await keycloakApi.authenticatedApiCall<{
      signed_url: string;
      expires_in: number;
    }>(`/payments/${id}/receipt`, { method: 'GET' });
  } catch {
    return null;
  }
};

export const getPaymentEventLog = async (id: string): Promise<EventLogEntry[]> => {
  return keycloakApi.authenticatedApiCall<EventLogEntry[]>(`/payments/${id}/event-log`, {
    method: 'GET',
  });
};

export const getFileEventLog = async (fileId: string): Promise<EventLogEntry[]> => {
  return keycloakApi.authenticatedApiCall<EventLogEntry[]>(`/payments/files/${fileId}/event-log`, {
    method: 'GET',
  });
};

/**
 * Update payment file status
 * @param fileId - File UUID
 * @param statusTypeId - New status type ID (from FILE_STATUS constants)
 * @returns Success response with updated file info
 */
export const updatePaymentFileStatus = async (
  fileId: string,
  statusTypeId: FileStatusId
): Promise<{
  success: boolean;
  message: string;
  data: { file_id: string; status_type_id: FileStatusId };
}> => {
  return keycloakApi.authenticatedApiCall<{
    success: boolean;
    message: string;
    data: { file_id: string; status_type_id: FileStatusId };
  }>(`/payments/files/${fileId}/status?status_type_id=${statusTypeId}`, {
    method: 'PUT',
  });
};
